export class MediaStreamer {
    constructor(mediaElement, type, manifestManager, httpClient) {
        this._destroyed = false;
        this._eventListeners = new Map();
        this._httpClient = httpClient;
        this._mediaElement = mediaElement;
        this._type = type;
        this._manifestManager = manifestManager;
    }
    /**
     * Initializes a MediaSource and returns the new `src` value.
     * Starts the stream when the source is opened.
     */
    init_m() {
        console.debug(`[MediaStreamer:${this._type}] init`);
        const codecs = this._manifestManager.getCodecs_m(this._type);
        const MIME_CODEC = `${this._type}/mp4; codecs="${codecs}"`;
        this._mediaSource = window.ManagedMediaSource
            ? new window.ManagedMediaSource()
            : new MediaSource();
        this._mediaSource.addEventListener('error', error => {
            console.error('MediaSource error:', error);
        });
        this._mediaSource.addEventListener('sourceopen', async () => {
            this._mediaSource.duration = this._manifestManager.getTotalDuration_m();
            console.debug(`[MediaStreamer:${this._type}] MediaSource open, duration: `, this._mediaSource.duration);
            this._sourceBuffer = this._mediaSource.addSourceBuffer(MIME_CODEC);
            try {
                await this._startStream();
            }
            catch (err) {
                console.error('Error starting media stream', err);
            }
        });
        this._mediaSrc = URL.createObjectURL(this._mediaSource);
        return this._mediaSrc;
    }
    destroy_m() {
        this._destroyed = true;
        this._eventListeners.clear();
        if (this._mediaSrc) {
            URL.revokeObjectURL(this._mediaSrc);
        }
    }
    on_m(event, callback) {
        var _a;
        if (!this._eventListeners.has(event)) {
            this._eventListeners.set(event, []);
        }
        (_a = this._eventListeners.get(event)) === null || _a === void 0 ? void 0 : _a.push(callback);
    }
    _emit(event, ...args) {
        const listeners = this._eventListeners.get(event);
        if (listeners) {
            listeners.forEach(listener => listener(...args));
        }
    }
    /**
     * Starts the chunk fetching and buffer process.
     * @private
     */
    async _startStream() {
        const baseUrl = this._manifestManager.getBaseUrl_m();
        await this._loadInitialChunk(baseUrl);
        await this._loadChunks(baseUrl);
    }
    /**
     * Loads the initialization chunk and appends it to the source buffer.
     * The initialization chunk contains metadata required to initialize the media decoder.
     * @param baseUrl
     * @private
     */
    async _loadInitialChunk(baseUrl) {
        const initializationUrl = this._manifestManager.getInitUrl_m(baseUrl, this._type);
        console.debug(`[MediaStreamer:${this._type}] loading initial chunk`);
        await this._httpClient.fetchChunkAndAppend_m(initializationUrl, this._sourceBuffer);
    }
    async _loadChunks(baseUrl) {
        const totalChunks = this._manifestManager.getTotalChunks_m();
        let chunkIndex = 0;
        console.debug(`[MediaStreamer:${this._type}] loading ${totalChunks} chunk(s)`);
        while (chunkIndex < totalChunks) {
            if (this._destroyed) {
                console.debug(`[MediaStreamer:${this._type}] destroyed, aborting chunk loading`);
                return;
            }
            const chunkUrl = this._manifestManager.getChunkUrl_m(baseUrl, chunkIndex, this._type);
            console.debug(`[MediaStreamer:${this._type}] Next chunk: ${chunkIndex + 1}/${totalChunks}`);
            try {
                if (this._sourceBuffer.updating) {
                    console.error('sourceBuffer should already been updated.');
                }
                if (this._mediaElement.error) {
                    console.error(this._mediaElement.error);
                }
                await this._httpClient.fetchChunkAndAppend_m(chunkUrl, this._sourceBuffer);
                this._manifestManager.verifyCurrentRepresentation_m(this._httpClient.bps, this._type);
                chunkIndex++;
            }
            catch (err) {
                console.error('Error fetching and appending chunk', err);
                break;
            }
        }
        console.debug(`[MediaStreamer: ${this._type}] Fetched all chunks: ${totalChunks}`);
        if (this._destroyed || this._mediaSource.readyState !== 'open') {
            return;
        }
        this._mediaSource.endOfStream();
        this._emit('chunksLoaded');
    }
}
